import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { numberToUSDCurrencyStringValue } from '../../../../models/UiUtils';
import { invalidateInsurancePolicyFilter, useInsurancePolicyFilter } from '../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Badge } from '../../dataBoards/Badge';
import { DeleteModal, DeleteType } from '../../dataBoards/DeleteModal';
import { EditPolicyModal } from '../../dataBoards/policies/EditPolicyModal';
import { LoadingSpinner } from '../../LoadingSpinner';
import { SimpleTooltip } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { styleSheet } from './styles';

enum MenuItem {
	Edit = 'Edit',
	Delete = 'Delete',
}

export const CompactPoliciesInfo = ({
	contact,
	rowStyles,
}: {
	contact: Api.IContact;
	rowStyles?: StyleDeclarationValue[];
}) => {
	const userSession = useUserSession();
	const insuranceIntegrationEnabled = userSession.hasInsuranceIntegrationConfigured;
	const canEdit = !insuranceIntegrationEnabled;

	const [policyToEdit, setPolicyToEdit] = React.useState<Api.IInsurancePolicy>(null);
	const [policyToDelete, setPolicyToDelete] = React.useState<Api.IInsurancePolicy>(null);

	const policyFilterQuery = useInsurancePolicyFilter({
		enabled: !!contact.id,
		filterRequest: {
			criteria: [
				{
					property: Api.InsurancePolicyFilterProperty.Contact,
					value: contact.id,
				},
			],
		},
	});

	const policies = React.useMemo(() => {
		return policyFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [policyFilterQuery.data]);

	const formatDateOrNull = (dateString?: string) => {
		return dateString ? moment(dateString).format('MM/DD/YYYY') : '-';
	};

	const editPolicy = (policy: Api.IInsurancePolicy) => {
		setPolicyToEdit(policy);
	};

	const deletePolicy = (policy: Api.IInsurancePolicy) => {
		setPolicyToDelete(policy);
	};

	const refreshPolicies = () => {
		setPolicyToEdit(null);
		setPolicyToDelete(null);
		invalidateInsurancePolicyFilter({
			filterRequest: {
				criteria: [
					{
						property: Api.InsurancePolicyFilterProperty.Contact,
						value: contact.id,
					},
				],
			},
		});
	};

	const onDeleteConfirmed = () => {
		refreshPolicies();
	};

	return (
		<div className={css(styleSheet.container)}>
			{policyFilterQuery.isPending ? <LoadingSpinner /> : null}

			{!policyFilterQuery.isPending && !policies.length ? (
				<p className={css(styleSheet.emptyText)}>No policies exist for this contact.</p>
			) : null}

			{policies.map(policy => {
				return (
					<div key={`policy-details-${policy.id}`} className={css(styleSheet.row, ...(rowStyles || []))}>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Policy</label>
							{policy.lineOfBusiness ? (
								<SimpleTooltip title={policy.lineOfBusiness.name} styles={[tooltipStyles.contentWithMaxWidth]}>
									<div>
										<Badge text={policy.lineOfBusiness.name} foregroundColor={policy.lineOfBusiness.foregroundColor} />
									</div>
								</SimpleTooltip>
							) : null}
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Renewal Date</label>
							<span>{formatDateOrNull(policy.renewalDate)}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Effective Date</label>
							<span>{formatDateOrNull(policy.effectiveDate)}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Origination Date</label>
							<span>{formatDateOrNull(policy.originationDate)}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Policy #</label>
							<span>{policy.policyNumber}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Carrier</label>
							<span>{policy.carrier.name}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Premium</label>
							<span>{policy.premium ? `${numberToUSDCurrencyStringValue(policy.premium)}` : '-'}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Owner</label>
							<span>
								{policy.owner ? `${policy.owner.firstName ?? ''} ${policy.owner.lastName ?? ''}`.trim() || '-' : '-'}
							</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Commissions</label>
							<span>{policy.commissions != null ? `${numberToUSDCurrencyStringValue(policy.commissions)}` : '-'}</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>CSR</label>
							<span>
								{policy.servicingRole1User
									? [policy.servicingRole1User.firstName, policy.servicingRole1User.lastName].join(' ')
									: '-'}
							</span>
						</div>
						<div className={css(styleSheet.box)}>
							<label className={css(styleSheet.label)}>Producer</label>
							<span>
								{policy.servicingRole2User
									? [policy.servicingRole2User.firstName, policy.servicingRole2User.lastName].join(' ')
									: '-'}
							</span>
						</div>
						{canEdit ? (
							<div className={css(styleSheet.actionBox)}>
								<Dropdown>
									<DropdownEllipsisTrigger />
									<DropdownContent>
										<DropdownItem onClick={() => editPolicy(policy)}>{MenuItem.Edit}</DropdownItem>
										<DropdownItem onClick={() => deletePolicy(policy)}>{MenuItem.Delete}</DropdownItem>
									</DropdownContent>
								</Dropdown>
							</div>
						) : null}
					</div>
				);
			})}

			{policyToEdit ? (
				<EditPolicyModal
					initialPolicy={policyToEdit}
					isOpen={policyToEdit !== null}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setPolicyToEdit(null);
						}
					}}
					onSave={refreshPolicies}
					onCancel={refreshPolicies}
				/>
			) : null}

			{policyToDelete ? (
				<DeleteModal
					type={DeleteType.Policy}
					item={policyToDelete}
					isOpen={policyToDelete !== null}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setPolicyToDelete(null);
						}
					}}
					onConfirm={onDeleteConfirmed}
				/>
			) : null}
		</div>
	);
};
